'use client';
/**
 * SPM 终端解析、查表与规则化编码工具。
 *
 * 全局框架组件（Header/Footer/侧边导航/面包屑）的 B 位跟随当前页面。
 * useGlobalSpmTerminal 解析页面 B 位；getSpmPositionCode 沿用 global_* 表的
 * 「模块 C + 坑位 D」模板并替换 B 位，避免商详等页面表未收录全局模块时漏码。
 * 没有页面 B 位或查无模块/坑位时返回 undefined，调用方不附加编码。
 *
 * 例外：顶部导航、底部导航、侧边导航的位置坑位（nav_item_XX、submenu_item_GG_II、
 * footer_link_XX、footer_group_toggle_XX 等）按坑位序号规则出码
 * （buildSpmPositionCode），不受表快照约束——表是生产基线的核验参照，CMS 内容
 * 增删条目时序号照规则连续编码；固定语义坑位（logo_entry、footer_help 等）仍查表。
 *
 * 页面楼层组件（首页/列表/商详楼层，B 位跟随所在页面）仍走
 * useSpmPageTerminal：先按「当前路由 + 终端」解析页面终端，再查表。
 *
 * 终端归属（pc / wap / app）统一由 reporting/spm/terminal.ts 判定：App WebView
 * 内嵌时 B 位上报 <页面>_app 而非 <页面>_wap。
 */
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { getSpmPageCodes, type SpmPageId } from '@/reporting/spm';
import { resolveSpmPositionCode } from '@/reporting/spm/position-code';
import { useSpmTerminalKind } from '@/reporting/spm/spm-dom';
import { getSpmSitePrefix } from '@/reporting/spm/site';
import { resolveSpmTerminalKind } from '@/reporting/spm/terminal';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useGoodsListSpmGroup } from '@/reporting/spm/goods-list-group';

type CurrentSpmPage = SpmPageId | `product_list_${string}_${'pc' | 'wap' | 'app'}`;

/**
 * 已接入 SPM 的路由 → 页面终端映射；其余路由返回 null（不附加编码）。
 * 纯函数版供测试与非 hook 场景使用；渲染期请用 useSpmPageTerminal（App 内嵌
 * 判定走 Provider 下发的服务端终端段，SSR 与客户端同值）。
 */
export function resolveSpmPageTerminal(
  pathname: string | null | undefined,
  deviceType: EPlatform,
): SpmPageId | null {
  if (!pathname) return null;
  if (pathname === '/') {
    return `home_${resolveSpmTerminalKind(deviceType)}`;
  }
  if (pathname === '/coupons') {
    return `coupons_${resolveSpmTerminalKind(deviceType)}`;
  }
  if (pathname === '/goods-list' || pathname.startsWith('/goods-list/')) {
    return `product_list_${resolveSpmTerminalKind(deviceType)}`;
  }
  if (pathname.startsWith('/goods-detail/')) {
    return `product_detail_${resolveSpmTerminalKind(deviceType)}`;
  }
  return null;
}

/** 当前路由 + 终端对应的页面终端；未接入页面为 null。 */
export function useSpmPageTerminal(deviceType: EPlatform): CurrentSpmPage | null {
  const { pathname } = useRouteMetadata();
  const kind = useSpmTerminalKind(deviceType);
  const groupId = useGoodsListSpmGroup(pathname);
  if (!pathname) return null;
  if (pathname === '/') return `home_${kind}`;
  if (pathname === '/coupons') return `coupons_${kind}`;
  if (pathname === '/goods-list' || pathname.startsWith('/goods-list/'))
    return groupId ? `product_list_${groupId}_${kind}` : null;
  if (pathname.startsWith('/goods-detail/')) return `product_detail_${kind}`;
  return null;
}

/**
 * 全局模块沿用 global_* 模板的 C/D，其余模块查当前页面表；B 位统一使用传入页面。
 * 查无页面/模块/坑位均返回 undefined，由调用方决定不附加（表外）。
 */
export function getSpmPositionCode(
  page: CurrentSpmPage | null,
  moduleCode: string,
  position: string,
): string | undefined {
  if (!page) return undefined;
  const templatePage = page.endsWith('_pc')
    ? 'global_pc'
    : page.endsWith('_app')
      ? 'global_app'
      : 'global_wap';
  const globalModule = getSpmPageCodes(templatePage)[moduleCode];
  const lookupPage = page.startsWith('product_list_')
    ? (`product_list_${page.endsWith('_pc') ? 'pc' : page.endsWith('_app') ? 'app' : 'wap'}` as SpmPageId)
    : (page as SpmPageId);
  const sourcePage = globalModule ? templatePage : lookupPage;
  const moduleCodes = globalModule ?? getSpmPageCodes(lookupPage)[moduleCode];
  const code = resolveSpmPositionCode(moduleCodes, position);
  return code?.replace(`.${sourcePage}.`, `.${page}.`);
}

/**
 * 按坑位序号规则生成完整编码（A.B.C.D）：导航类位置坑位（nav_item_XX、
 * submenu_item_GG_II、footer_link_XX 等）按渲染序号推导，表外坑位照常出码，
 * 保证 CMS 条目增删后编码仍随位置连续。仅用于导航类位置坑位；固定语义坑位
 * 用 getSpmPositionCode 查表（保留表缺失即不上报的口径）。
 */
export function buildSpmPositionCode(
  page: CurrentSpmPage | null,
  moduleCode: string,
  position: string,
): string | undefined {
  if (!page) return undefined;
  return `${getSpmSitePrefix()}.${page}.${moduleCode}.${position}`;
}

export function resolveGlobalSpmTerminal(
  deviceType: EPlatform,
): 'global_pc' | 'global_wap' | 'global_app' {
  return `global_${resolveSpmTerminalKind(deviceType)}`;
}

/**
 * 全局组件使用当前页面 B 位；终端判定与页面共用 Provider，切页时同步更新。
 */
export function useGlobalSpmTerminal(deviceType: EPlatform): CurrentSpmPage | null {
  return useSpmPageTerminal(deviceType);
}

/** D 位坑位序号统一两位数字（01、02…），与编码表命名一致。 */
export function padSpmPositionIndex(index: number): string {
  return String(index).padStart(2, '0');
}
